import React, { useEffect, useState } from "react";
import axios from "axios";
import BrandLogo from "../components/BrandLogo";
import ThemeToggle from "../components/ThemeToggle";
import TrustCenterStatusStrip from "../components/TrustCenterStatusStrip";
import { Card, CardContent } from "../components/ui/card";
import {
  ShieldCheck, Lock, Users, Siren, GraduationCap, Mail, CheckCircle2, Building2,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HIGHLIGHT_ICONS = [Lock, Users, Siren, GraduationCap, Building2];

const STATUS_CLS = {
  "Strong coverage": "text-emerald-600 border-emerald-500/40 bg-emerald-500/10",
  "In progress": "text-sky-600 border-sky-500/40 bg-sky-500/10",
  "Early stage": "text-amber-600 border-amber-500/40 bg-amber-500/10",
};

export default function TrustCenter() {
  const [data, setData] = useState(null);

  useEffect(() => {
    axios.get(`${API}/trust-center/overview`).then((r) => setData(r.data)).catch(() => setData(null));
  }, []);

  return (
    <div className="min-h-screen bg-background" data-testid="trust-center-page">
      <header className="border-b border-border">
        <div className="mx-auto max-w-[1000px] px-4 sm:px-6 h-20 flex items-center justify-between">
          <BrandLogo variant="full" className="h-12 w-auto" />
          <ThemeToggle />
        </div>
      </header>

      <main className="mx-auto max-w-[1000px] px-4 sm:px-6 py-10 sm:py-14 space-y-10">
        <section className="text-center space-y-3">
          <div className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-primary" data-testid="trust-center-badge">
            <ShieldCheck className="w-3.5 h-3.5" /> Trust Center
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold">Security &amp; compliance, at a glance</h1>
          <p className="text-base text-muted-foreground max-w-xl mx-auto">
            An overview of how UnitedCloud protects customer data — the frameworks we align to,
            the vendors we work with, and how to reach our security team.
          </p>
        </section>

        <TrustCenterStatusStrip />

        {!data ? (
          <div className="h-64 rounded-xl bg-muted/40 animate-pulse" />
        ) : (
          <>
            <section data-testid="trust-center-frameworks">
              <h2 className="text-lg font-semibold mb-3">Frameworks we align to</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {data.frameworks.map((f) => (
                  <Card key={f.key} data-testid={`trust-center-framework-${f.key}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <div className="text-[14px] font-semibold">{f.name}</div>
                          <div className="text-[11.5px] text-muted-foreground">{f.subtitle}</div>
                        </div>
                        <span className={`text-[10px] font-semibold uppercase rounded-full border px-2 py-0.5 shrink-0 ${STATUS_CLS[f.status_label]}`}>{f.status_label}</span>
                      </div>
                      <p className="text-[12.5px] text-muted-foreground mt-2">{f.description}</p>
                      <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full bg-primary rounded-full" style={{ width: `${f.maturity_pct}%` }} />
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </section>

            <section data-testid="trust-center-highlights">
              <h2 className="text-lg font-semibold mb-3">How we protect your data</h2>
              <div className="grid sm:grid-cols-2 gap-3">
                {data.security_highlights.map((h, i) => {
                  const Icon = HIGHLIGHT_ICONS[i % HIGHLIGHT_ICONS.length];
                  return (
                    <div key={i} className="flex items-start gap-2.5 rounded-lg border border-border bg-card p-3.5">
                      <Icon className="w-4 h-4 text-primary shrink-0 mt-0.5" />
                      <span className="text-[13px]">{h}</span>
                    </div>
                  );
                })}
              </div>
            </section>

            <section data-testid="trust-center-vendors">
              <h2 className="text-lg font-semibold mb-3">Subprocessors &amp; vendors</h2>
              <div className="rounded-xl border border-border bg-card divide-y divide-border">
                {data.vendors.map((v) => (
                  <div key={v.name} className="flex items-center justify-between px-4 py-2.5" data-testid={`trust-center-vendor-${v.name.replace(/\s+/g, "-")}`}>
                    <div>
                      <div className="text-[13px] font-medium">{v.name}</div>
                      <div className="text-[11.5px] text-muted-foreground">{v.category}</div>
                    </div>
                    {v.has_soc2 && (
                      <span className="inline-flex items-center gap-1 text-[11px] font-medium text-emerald-600">
                        <CheckCircle2 className="w-3.5 h-3.5" /> SOC 2 report on file
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </section>

            <section className="text-center" data-testid="trust-center-contact">
              <h2 className="text-lg font-semibold mb-2">Questions about our security?</h2>
              <a href={`mailto:${data.contact_email}`} className="inline-flex items-center gap-2 text-primary font-medium hover:underline">
                <Mail className="w-4 h-4" /> {data.contact_email}
              </a>
            </section>
          </>
        )}
      </main>

      <footer className="text-center py-8 text-[12px] text-muted-foreground">
        &copy; 2026 UnitedCloud &middot; Trust Center
      </footer>
    </div>
  );
}
